// Quiz data - owner adds quizzes via code
// Employees/leads must complete at least 3 quizzes per month

export const MONTHLY_QUIZ_REQUIREMENT = 3;

export const quizzes = [
  {
    id: 'quiz_cleaning_chemicals_101',
    title: 'Cleaning Chemicals Safety',
    description: 'Test your knowledge of safe chemical handling, mixing rules, and proper storage for common cleaning products.',
    category: 'safety',
    difficulty: 'beginner',
    passingScore: 70,
    timeEstimate: '5 min',
    questions: [
      {
        id: 'q1',
        text: 'What should you NEVER do when mixing cleaning products?',
        options: [
          'Read the product label first',
          'Mix bleach with ammonia-based cleaners',
          'Wear gloves while handling chemicals',
          'Open a window for ventilation'
        ],
        correctIndex: 1,
        explanation: 'Mixing bleach with ammonia creates toxic chloramine gas which can cause serious respiratory harm. Never combine these chemicals.'
      },
      {
        id: 'q2',
        text: 'When entering a client\'s home, where should you store your cleaning caddy?',
        options: [
          'On the kitchen counter',
          'On the floor near the front door',
          'On a towel or mat to protect surfaces',
          'Wherever is most convenient'
        ],
        correctIndex: 2,
        explanation: 'Always place your caddy on a towel or mat to prevent scratches or chemical drips on client surfaces.'
      },
      {
        id: 'q3',
        text: 'What is the proper way to dilute a concentrated cleaning solution?',
        options: [
          'Pour the water into the chemical',
          'Mix it by feel — more soap means cleaner',
          'Follow the manufacturer\'s dilution ratio on the label',
          'Use hot water to make it stronger'
        ],
        correctIndex: 2,
        explanation: 'Always follow the label dilution ratios. Over-concentration wastes product and can damage surfaces; under-dilution won\'t clean effectively.'
      },
      {
        id: 'q4',
        text: 'If a client says they are sensitive to fragrances, what should you do?',
        options: [
          'Use less of the scented product',
          'Switch to our fragrance-free product line and note it on the property profile',
          'Tell them the scent will fade quickly',
          'Spray air freshener after to mask it'
        ],
        correctIndex: 1,
        explanation: 'Always respect client sensitivities. Switch to fragrance-free products and document the preference so every team visit follows the same protocol.'
      },
      {
        id: 'q5',
        text: 'How long should a disinfectant typically sit on a surface to be effective (dwell time)?',
        options: [
          'No wait needed — wipe immediately',
          'At least 30 seconds',
          'Follow the product label — usually 1-10 minutes',
          'At least 30 minutes'
        ],
        correctIndex: 2,
        explanation: 'Dwell time varies by product. The label specifies how long the disinfectant must remain wet on the surface to kill germs. Always check and follow it.'
      }
    ],
    status: 'active',
    createdAt: '2026-01-15T10:00:00.000Z',
    updatedAt: '2026-01-15T10:00:00.000Z'
  },
  {
    id: 'quiz_client_home_professionalism',
    title: 'Professionalism in Client Homes',
    description: 'Review the standards for professional behavior, communication, and conduct when working in a client\'s home.',
    category: 'professionalism',
    difficulty: 'beginner',
    passingScore: 70,
    timeEstimate: '5 min',
    questions: [
      {
        id: 'q1',
        text: 'You arrive at a client\'s home and their dog runs to the door. What is the correct response?',
        options: [
          'Let the dog outside so it\'s out of the way',
          'Greet the pet calmly, check the property notes for pet instructions, and follow them',
          'Ignore the pet entirely',
          'Give the pet a treat from your bag'
        ],
        correctIndex: 1,
        explanation: 'Always check property notes for pet instructions (some pets need to be kept in a specific room, etc.). Be calm and friendly but follow the documented instructions.'
      },
      {
        id: 'q2',
        text: 'A client left a $50 bill on the kitchen counter. What should you do?',
        options: [
          'Leave it exactly where it is and clean around it',
          'Move it to a safe spot and let the client know',
          'Leave it and take a photo for documentation',
          'Assume it\'s a tip and take it'
        ],
        correctIndex: 0,
        explanation: 'Never move or take cash left out. Clean around it. If you believe it\'s a tip, confirm with the office. We never assume.'
      },
      {
        id: 'q3',
        text: 'What should you do if you accidentally break a client\'s item?',
        options: [
          'Hide the broken pieces and hope they don\'t notice',
          'Replace it yourself before they find out',
          'Report it to your team lead and the office immediately, and document it with a photo',
          'Leave a note apologizing'
        ],
        correctIndex: 2,
        explanation: 'Honesty and transparency build trust. Report damage immediately to your team lead and the office. Take a photo for documentation. We handle it professionally.'
      },
      {
        id: 'q4',
        text: 'You notice signs of a pest problem (roaches, ants, etc.) at a client\'s property. What do you do?',
        options: [
          'Tell the client directly that their home is infested',
          'Ignore it — it\'s not your job',
          'Note it in the job completion notes so the office can tactfully communicate with the client',
          'Post about it to the team group chat'
        ],
        correctIndex: 2,
        explanation: 'Document observations professionally in your job notes. The office will handle sensitive communication with the client. Never discuss client issues on group chats or social media.'
      },
      {
        id: 'q5',
        text: 'What is the Two Sisters policy on using a client\'s personal items (phone charger, snacks, bathroom)?',
        options: [
          'It\'s fine as long as you ask first',
          'Never use a client\'s personal belongings, food, or facilities unless the client has explicitly offered and it\'s noted in property instructions',
          'You can use the bathroom but nothing else',
          'It depends on how well you know the client'
        ],
        correctIndex: 1,
        explanation: 'We maintain professional boundaries. Do not use client belongings, food, or facilities unless explicitly offered and documented in property notes.'
      }
    ],
    status: 'active',
    createdAt: '2026-01-15T10:00:00.000Z',
    updatedAt: '2026-01-15T10:00:00.000Z'
  },
  {
    id: 'quiz_deep_clean_procedures',
    title: 'Deep Clean Procedures & Standards',
    description: 'Make sure you know the step-by-step process and quality standards for our deep cleaning services.',
    category: 'procedures',
    difficulty: 'intermediate',
    passingScore: 80,
    timeEstimate: '6 min',
    questions: [
      {
        id: 'q1',
        text: 'What is the correct order for cleaning a bathroom during a deep clean?',
        options: [
          'Toilet → mirror → floor → shower',
          'Top to bottom: light fixtures/vents → mirrors → counters → toilet → tub/shower → floor',
          'Whatever order feels fastest',
          'Floor first so it\'s dry by the time you finish'
        ],
        correctIndex: 1,
        explanation: 'Always clean top to bottom. Dust and drips fall downward, so starting high and finishing with floors ensures you don\'t re-contaminate cleaned surfaces.'
      },
      {
        id: 'q2',
        text: 'During a deep clean, what areas should you clean that are NOT part of a standard recurring clean?',
        options: [
          'Inside the oven and refrigerator',
          'Baseboards, inside window sills, light switch plates, door frames, and vent covers',
          'Only the areas the client specifically mentions',
          'The garage and outdoor patio'
        ],
        correctIndex: 1,
        explanation: 'Deep cleans include all standard areas PLUS baseboards, window sills, switch plates, door frames, vent covers, ceiling fan blades, and other detail areas. Inside appliances are an add-on service.'
      },
      {
        id: 'q3',
        text: 'You\'re running behind on a deep clean. The scheduled time is almost up but you haven\'t finished. What should you do?',
        options: [
          'Rush through the remaining rooms',
          'Skip the less visible areas to finish on time',
          'Notify your team lead immediately and communicate the situation — never cut quality',
          'Leave and tell the office you finished'
        ],
        correctIndex: 2,
        explanation: 'Quality is never sacrificed for speed. Communicate early if you\'re running behind so the team lead and office can adjust scheduling or send help.'
      },
      {
        id: 'q4',
        text: 'What photos are required when completing a deep clean job in the app?',
        options: [
          'No photos are needed',
          'Only "after" photos of the kitchen',
          'Before AND after photos of key areas (kitchen, bathrooms, main living spaces)',
          'One photo of the front of the house'
        ],
        correctIndex: 2,
        explanation: 'Before and after photos document the work quality, protect against disputes, and help the office provide great service. Always take them for key areas.'
      },
      {
        id: 'q5',
        text: 'A first-time deep clean client has a home that is much dirtier than expected. What is the best approach?',
        options: [
          'Clean what you can and leave the rest',
          'Call the office immediately to discuss — the estimate may need adjusting, and additional time/help may be needed',
          'Complain to the client about the condition',
          'Charge them extra on the spot'
        ],
        correctIndex: 1,
        explanation: 'If a home\'s condition doesn\'t match the estimate, communicate with the office right away. They can adjust the scope, send help, or update the client. Never negotiate pricing directly.'
      },
      {
        id: 'q6',
        text: 'When cleaning kitchen cabinets during a deep clean, what should you use on wood cabinet faces?',
        options: [
          'Bleach spray for maximum cleanliness',
          'A damp microfiber cloth with a gentle all-purpose cleaner, then dry immediately',
          'Steel wool to remove grease buildup',
          'Furniture polish to make them shiny'
        ],
        correctIndex: 1,
        explanation: 'Wood cabinets need gentle care. Use a damp microfiber cloth with mild cleaner, wipe with the wood grain, and dry immediately to prevent water damage. Avoid harsh chemicals and abrasives.'
      }
    ],
    status: 'active',
    createdAt: '2026-02-01T10:00:00.000Z',
    updatedAt: '2026-02-01T10:00:00.000Z'
  },
  {
    id: 'quiz_tools_products_handling',
    title: 'Tools & Products: Proper Handling',
    description: 'Know when and how to safely use specialty tools like pumice stones, magic erasers, razors, and our cleaning products on the right surfaces.',
    category: 'procedures',
    difficulty: 'intermediate',
    passingScore: 80,
    timeEstimate: '6 min',
    questions: [
      {
        id: 'q1',
        text: 'When is it appropriate to use a pumice stone during a clean?',
        options: [
          'To scrub soap scum off glass shower doors',
          'To remove hard water rings and mineral buildup inside a toilet bowl',
          'To clean stainless steel sinks',
          'To scrub grout on tile floors'
        ],
        correctIndex: 1,
        explanation: 'Pumice stones are safe for removing mineral rings and hard water buildup inside porcelain toilet bowls. Never use them on glass, stainless steel, tile, or any coated/polished surface — they will scratch.'
      },
      {
        id: 'q2',
        text: 'What surfaces should you NEVER use a Magic Eraser on?',
        options: [
          'Painted walls with flat or matte paint, wood finishes, and stainless steel',
          'Tile backsplash and laminate countertops',
          'Interior doors and baseboards',
          'Light switch plates and outlet covers'
        ],
        correctIndex: 0,
        explanation: 'Magic Erasers are micro-abrasives. They will dull or remove flat/matte paint, strip wood finishes, and scratch stainless steel. They\'re great for scuffs on semi-gloss surfaces, stovetops, and hard non-porous surfaces.'
      },
      {
        id: 'q3',
        text: 'When using a razor blade scraper on glass (e.g., stovetop or shower door), what must you do first?',
        options: [
          'Nothing — just start scraping',
          'Wet the surface with cleaner first so the blade glides, and use the razor at a shallow angle',
          'Apply The Pink Stuff paste before scraping',
          'Test on a small area of the wall nearby'
        ],
        correctIndex: 1,
        explanation: 'Always wet the glass surface with cleaner before using a razor scraper, and hold the blade at a shallow 20-30 degree angle. A dry surface or steep angle will scratch the glass. Only use razors on glass — never on other surfaces.'
      },
      {
        id: 'q4',
        text: 'What is The Pink Stuff paste best used for?',
        options: [
          'Polishing wood furniture',
          'Disinfecting bathroom floors',
          'Gently scrubbing tough stains on sinks, stovetops, and ceramic surfaces',
          'Cleaning glass and mirrors for a streak-free shine'
        ],
        correctIndex: 2,
        explanation: 'The Pink Stuff is a gentle abrasive cleaning paste perfect for sinks, stovetops, ceramic, and enamel surfaces. It removes tough stains without harsh chemicals. Avoid using it on high-gloss or coated surfaces.'
      },
      {
        id: 'q5',
        text: 'A client has hardwood floors. Which floor cleaner should you use?',
        options: [
          'Simple Green diluted in the mop bucket',
          'OdoBan — our standard pH-neutral floor cleaner, or Bona if the client requests it',
          'Citrus Degreaser for extra shine',
          'Just hot water — soap damages wood'
        ],
        correctIndex: 1,
        explanation: 'OdoBan is our default pH-neutral floor cleaner safe for all sealed hard floors including hardwood. If the client prefers Bona (specifically formulated for hardwood), use that instead. Never use degreasers or harsh chemicals on wood.'
      },
      {
        id: 'q6',
        text: 'You need to remove heavy grease buildup on a range hood. Which product do you reach for?',
        options: [
          'Custom Glass & Mirror Cleaner',
          'Bee\'s Wax Polish',
          'Citrus Degreaser or Simple Green Degreaser (light dilution)',
          'Magic Eraser only'
        ],
        correctIndex: 2,
        explanation: 'Citrus Degreaser is our heavy-duty option for stubborn kitchen grease. Simple Green Degreaser (less diluted) also works well. Let the degreaser sit for a minute, then wipe. Glass cleaner and polish are not degreasers.'
      },
      {
        id: 'q7',
        text: 'What is Bee\'s Wax Polish used for?',
        options: [
          'Disinfecting countertops after food prep',
          'Polishing and protecting wood, glass, and countertop surfaces',
          'Cleaning bathroom tile grout',
          'Removing hard water stains from faucets'
        ],
        correctIndex: 1,
        explanation: 'Bee\'s Wax Polish is our natural beeswax product for polishing and adding a protective layer to wood furniture, glass tabletops, and countertops. It is not a cleaner or disinfectant.'
      }
    ],
    status: 'active',
    createdAt: '2026-02-15T10:00:00.000Z',
    updatedAt: '2026-02-15T10:00:00.000Z'
  },
  {
    id: 'quiz_recurring_clean_scope',
    title: 'Recurring Clean Scope & Standards',
    description: 'Test your knowledge of what\'s included in our weekly, bi-weekly, and monthly recurring cleans — room by room.',
    category: 'scope',
    difficulty: 'beginner',
    passingScore: 70,
    timeEstimate: '5 min',
    questions: [
      {
        id: 'q1',
        text: 'During a standard recurring clean, which of the following is included in the KITCHEN scope?',
        options: [
          'Cleaning inside the oven and refrigerator',
          'Wiping countertops, cleaning the stovetop, sanitizing the sink, and wiping appliance exteriors',
          'Washing dishes left in the sink',
          'Organizing pantry shelves and cabinets'
        ],
        correctIndex: 1,
        explanation: 'A recurring kitchen clean includes countertops, stovetop, sink, appliance exteriors, cabinet fronts (as needed), trash, and floors. Inside the oven and fridge are add-on services. We do not wash dishes or organize.'
      },
      {
        id: 'q2',
        text: 'In a recurring bathroom clean, which task is part of the standard scope?',
        options: [
          'Scrubbing tile grout and removing hard water buildup',
          'Cleaning and sanitizing the toilet inside and out, sink, mirror, and shower/tub surfaces',
          'Deep cleaning shower door tracks',
          'Organizing under-sink cabinets'
        ],
        correctIndex: 1,
        explanation: 'Standard recurring bathrooms include toilet, sink, countertop, mirror, shower/tub surfaces, cabinet fronts, trash, and floors. Grout scrubbing, track deep cleaning, and inside-cabinet work are deep clean or add-on tasks.'
      },
      {
        id: 'q3',
        text: 'A client on a bi-weekly plan asks you to clean inside their kitchen cabinets during a regular visit. What should you do?',
        options: [
          'Go ahead — it only takes a few extra minutes',
          'Explain that inside cabinets is not part of the recurring scope and let the office know the client is interested in adding it',
          'Skip it without saying anything',
          'Clean just the ones that look dirty'
        ],
        correctIndex: 1,
        explanation: 'Inside cabinets and drawers are not part of the recurring clean scope. Politely let the client know, and inform the office so they can offer it as an add-on or schedule a deep clean.'
      },
      {
        id: 'q4',
        text: 'What does "wipe baseboards on rotation as needed" mean for a recurring clean?',
        options: [
          'Wipe every baseboard in the house every single visit',
          'Never touch baseboards during recurring cleans',
          'Spot-wipe visible baseboards on a rotating basis — you don\'t need to do every room every time, but they should all get attention over multiple visits',
          'Only wipe baseboards if the client asks'
        ],
        correctIndex: 2,
        explanation: 'On recurring cleans, baseboards get attention on rotation. Focus on the most visible or dusty areas each visit, and rotate through the home so all baseboards stay maintained over time.'
      },
      {
        id: 'q5',
        text: 'Which of the following is TRUE about ceiling fans during a recurring clean?',
        options: [
          'Detail every ceiling fan blade on every visit',
          'Dust ceiling fans within reach — you don\'t need a tall ladder',
          'Ceiling fans are never part of a recurring clean',
          'Only clean ceiling fans in bedrooms'
        ],
        correctIndex: 1,
        explanation: 'Recurring cleans include dusting ceiling fans that are within reach (using a 6-foot duster or 3-step ladder). Anything beyond that height is not in scope.'
      },
      {
        id: 'q6',
        text: 'A client\'s home has no running water today due to plumbing work. What should you do?',
        options: [
          'Clean what you can without water',
          'Use bottled water from your car',
          'Contact the office — homes must have running water, electricity, and HVAC between 64-75°F for us to service them',
          'Wait at the property until the water comes back on'
        ],
        correctIndex: 2,
        explanation: 'Per our scope of service, homes must have running water, electricity, and HVAC between 64°F–75°F. If any of these are missing, contact the office to reschedule.'
      }
    ],
    status: 'active',
    createdAt: '2026-02-15T10:00:00.000Z',
    updatedAt: '2026-02-15T10:00:00.000Z'
  },
  {
    id: 'quiz_move_in_out_scope',
    title: 'Move In / Move Out Clean Standards',
    description: 'Understand what sets a move in/out clean apart from a deep clean — inside cabinets, appliances, closets, and the final walkthrough.',
    category: 'scope',
    difficulty: 'intermediate',
    passingScore: 80,
    timeEstimate: '5 min',
    questions: [
      {
        id: 'q1',
        text: 'What makes a move in/out clean different from a standard deep clean?',
        options: [
          'It\'s exactly the same as a deep clean',
          'Move in/out includes everything in a deep clean PLUS inside all cabinets, drawers, closets, oven, fridge, dishwasher, and a final walkthrough',
          'Move in/out only covers kitchens and bathrooms',
          'Move in/out skips bedrooms since they\'re empty'
        ],
        correctIndex: 1,
        explanation: 'A move in/out clean is our most comprehensive service. It includes everything in a deep clean PLUS inside all cabinets, drawers, closets (shelves, rods, walls), inside oven, fridge (if empty), dishwasher, pantry shelves, and a final walkthrough.'
      },
      {
        id: 'q2',
        text: 'During a move-out clean, you find items the previous tenant left behind in a closet. What do you do?',
        options: [
          'Throw them away to make the closet cleanable',
          'Move them to the garage and clean the closet',
          'Note it in the job and contact the office — removing items left behind is not in our scope',
          'Clean around them as best you can'
        ],
        correctIndex: 2,
        explanation: 'Moving or removing items left behind is explicitly not in our move in/out scope. Document it and contact the office so they can coordinate with the client or property manager.'
      },
      {
        id: 'q3',
        text: 'Which of the following IS included in a move in/out kitchen clean?',
        options: [
          'Only the stovetop and sink',
          'Inside all cabinets, drawers, oven, refrigerator (if empty), dishwasher, pantry shelves, range hood and filter, plus everything in a deep clean',
          'Just the countertops and floors',
          'Only what the property manager specifically requests'
        ],
        correctIndex: 1,
        explanation: 'Move in/out kitchens are fully detailed: inside all cabinets and drawers, inside oven, fridge (if empty), dishwasher, pantry shelves, and degreasing the range hood and filter — on top of the full deep clean scope.'
      },
      {
        id: 'q4',
        text: 'Are carpet shampooing and wall washing included in a move in/out clean?',
        options: [
          'Yes — it\'s our most thorough service so everything is included',
          'Only wall washing is included',
          'No — carpet shampooing, steam cleaning, wall washing, and scuff removal are all excluded from move in/out scope',
          'Only if the property manager requests it'
        ],
        correctIndex: 2,
        explanation: 'Carpet shampooing/steam cleaning, wall washing, and scuff removal are NOT included even in move in/out cleans. These are separate specialty services.'
      },
      {
        id: 'q5',
        text: 'What is the final step of a move in/out clean?',
        options: [
          'Take a photo of the kitchen only',
          'Lock up and leave immediately',
          'A final walkthrough inspection of the entire home to ensure every area meets standards',
          'Call the client to let them know you\'re done'
        ],
        correctIndex: 2,
        explanation: 'Every move in/out clean ends with a final walkthrough inspection. Walk every room, check closets, cabinets, and bathrooms to make sure nothing was missed and every area meets our quality standards.'
      },
      {
        id: 'q6',
        text: 'During a move-in clean, you\'re asked to also clean the garage. Is this in scope?',
        options: [
          'Yes — the full garage is part of every move in/out',
          'Only the garage entry area is in scope — a full garage deep clean is not included',
          'No — garages are never touched',
          'Only if the garage has finished floors'
        ],
        correctIndex: 1,
        explanation: 'The garage entry area (the transition area from garage to home) is included. A full garage deep clean is not part of the standard move in/out scope and would need to be quoted separately.'
      }
    ],
    status: 'active',
    createdAt: '2026-02-15T10:00:00.000Z',
    updatedAt: '2026-02-15T10:00:00.000Z'
  },
  {
    id: 'quiz_trash_disposal_policy',
    title: 'Trash Disposal Policy',
    description: 'We have received a formal warning from our landlord about trash. Know exactly where office and client trash goes — no exceptions.',
    category: 'policy',
    difficulty: 'beginner',
    passingScore: 100,
    timeEstimate: '3 min',
    questions: [
      {
        id: 'q1',
        text: 'Where must ALL office trash be disposed of?',
        options: [
          'Any trash can inside the office building',
          'The designated dumpster out back — no exceptions',
          'The recycling bin near the front entrance',
          'Any available receptacle around the building'
        ],
        correctIndex: 1,
        explanation: 'All office trash must go in the designated DUMPSTER OUT BACK — no exceptions. We have received a formal written warning from our landlord. Using other receptacles around the building may result in fines or jeopardize our lease.'
      },
      {
        id: 'q2',
        text: 'You just finished a cleaning job and have a bag of trash from the client\'s home. What do you do with it?',
        options: [
          'Bring it back to the office and put it in our dumpster',
          'Leave it in your car to throw away at home',
          'Dispose of it in the client\'s dumpster or bins at their property',
          'Drop it in a public trash can on the way back'
        ],
        correctIndex: 2,
        explanation: 'Client trash must ALWAYS be disposed of in the client\'s dumpster or bins at that location. Client trash must NEVER be brought back to the office or placed in our office dumpster.'
      },
      {
        id: 'q3',
        text: 'You\'re at a client\'s property and can\'t find their outdoor trash bins. What should you do?',
        options: [
          'Leave the trash bags inside the house near the front door',
          'Take the trash back to the office dumpster',
          'Ask the client or contact management before leaving',
          'Put it in a neighbor\'s bin'
        ],
        correctIndex: 2,
        explanation: 'If you\'re unsure where to dispose of trash at a client\'s property, ask the client or contact management before leaving. Never assume — and never bring client trash back to the office.'
      },
      {
        id: 'q4',
        text: 'The office dumpster out back is completely full. What do you do?',
        options: [
          'Stack trash bags on the ground next to the dumpster',
          'Use one of the other bins around the building',
          'Contact management to report the issue',
          'Take the trash home with you'
        ],
        correctIndex: 2,
        explanation: 'If the dumpster is full, contact management. Do NOT use other receptacles around the building and do NOT stack trash outside the dumpster. Management will arrange for a solution.'
      },
      {
        id: 'q5',
        text: 'Why is the office trash policy so strict right now?',
        options: [
          'It\'s just a suggestion to keep things tidy',
          'We received a formal written warning from our landlord — further violations may result in fines or jeopardize our lease',
          'The city changed recycling rules',
          'There is no strict policy — use any bin that\'s convenient'
        ],
        correctIndex: 1,
        explanation: 'We received a formal written warning from our landlord. This is serious — further violations may result in fines or jeopardize our lease. Everyone must follow the trash policy without exception.'
      }
    ],
    status: 'active',
    createdAt: '2026-02-18T10:00:00.000Z',
    updatedAt: '2026-02-18T10:00:00.000Z'
  },
  {
    id: 'quiz_contact_communication_policy',
    title: 'Contact & Communication Policy',
    description: 'Know who to contact, when, and how — from reporting issues at a job site to escalating emergencies and communicating with clients.',
    category: 'policy',
    difficulty: 'beginner',
    passingScore: 80,
    timeEstimate: '4 min',
    questions: [
      {
        id: 'q1',
        text: 'You accidentally damage a client\'s item during a clean. Who do you contact and when?',
        options: [
          'Tell the client directly and offer to pay for it yourself',
          'Don\'t tell anyone — just clean up and move on',
          'Report it to your team lead AND the office immediately, and take a photo for documentation',
          'Wait until the end of the day and send a text to the office'
        ],
        correctIndex: 2,
        explanation: 'Report damage immediately to your team lead and the office. Take a photo for documentation. The company handles damage claims professionally — never try to handle it yourself or hide it.'
      },
      {
        id: 'q2',
        text: 'A client asks you to do something outside of the normal scope of work (e.g., clean inside their garage, wash their car). What do you do?',
        options: [
          'Just do it to keep the client happy',
          'Politely let the client know that\'s outside your scheduled scope and contact the office so they can discuss it with the client',
          'Refuse and walk away',
          'Do it but complain about it later'
        ],
        correctIndex: 1,
        explanation: 'Never agree to scope changes on the spot. Politely explain it\'s outside the scheduled service and let the office handle it. The office can quote additional work, adjust the schedule, or communicate with the client.'
      },
      {
        id: 'q3',
        text: 'You\'re running 15+ minutes behind schedule and won\'t finish the job on time. What should you do?',
        options: [
          'Rush through the remaining rooms to finish on time',
          'Skip less visible areas to catch up',
          'Notify your team lead as soon as you realize, so the office can adjust scheduling or send help',
          'Just stay late and don\'t tell anyone'
        ],
        correctIndex: 2,
        explanation: 'Communicate early when you\'re running behind. Notify your team lead right away so the office can adjust the schedule, send help, or notify the next client. Never sacrifice quality or skip areas to catch up.'
      },
      {
        id: 'q4',
        text: 'A client tries to discuss pricing, discounts, or billing with you directly. What is the correct response?',
        options: [
          'Give them a discount to keep them happy',
          'Tell them the price is too high and you agree',
          'Let them know that all pricing and billing goes through the office, and offer to have the office reach out to them',
          'Ignore the question and change the subject'
        ],
        correctIndex: 2,
        explanation: 'Never discuss or negotiate pricing directly with clients. All pricing, billing, and account questions go through the office. Politely redirect them and offer to have the office follow up.'
      },
      {
        id: 'q5',
        text: 'You arrive at a client\'s home and notice something concerning (e.g., signs of a pest problem, unsafe conditions, items that look out of place). What do you do?',
        options: [
          'Post about it on the team group chat',
          'Tell the client their house has a problem',
          'Note it in the job completion notes so the office can handle communication with the client professionally',
          'Ignore it — it\'s none of your business'
        ],
        correctIndex: 2,
        explanation: 'Document observations in your job notes. The office handles all sensitive client communication. Never discuss concerns with the client directly, and never share client information on group chats or social media.'
      },
      {
        id: 'q6',
        text: 'You can\'t access a client\'s home (gate code doesn\'t work, nobody is home, lockbox is stuck). What do you do?',
        options: [
          'Wait for 30 minutes and then leave',
          'Try to find an unlocked window or back door',
          'Contact the office immediately so they can reach the client and get you access',
          'Skip the job and go to the next one'
        ],
        correctIndex: 2,
        explanation: 'Contact the office immediately with access issues. They will reach the client, get updated codes, or reschedule if needed. Never try to enter through unsecured entry points, and don\'t wait without communicating.'
      }
    ],
    status: 'active',
    createdAt: '2026-02-18T10:00:00.000Z',
    updatedAt: '2026-02-18T10:00:00.000Z'
  },
  {
    id: 'quiz_natural_stone_shower_cleaning',
    title: 'Natural Stone & Shower Cleaning',
    description: 'Know which products are safe for natural stone, the correct way to clean showers, and the rules for brush usage — mistakes here cause real damage.',
    category: 'procedures',
    difficulty: 'intermediate',
    passingScore: 80,
    timeEstimate: '5 min',
    questions: [
      {
        id: 'q1',
        text: 'What is the BEST product to use when cleaning natural stone surfaces?',
        options: [
          'Zep bathroom cleaner',
          'Tub and tile cleaner',
          'Heavily diluted Simple Green',
          'Undiluted Dawn dish soap'
        ],
        correctIndex: 2,
        explanation: 'Heavily diluted Simple Green is the best option for natural stone. A tiny bit of Dawn is acceptable in a pinch, but Simple Green (heavily diluted) is the preferred choice.'
      },
      {
        id: 'q2',
        text: 'Which products should you NEVER use on natural stone?',
        options: [
          'Simple Green (heavily diluted)',
          'Tub and tile cleaner or grout cleaner',
          'A tiny amount of Dawn',
          'Water and a microfiber cloth'
        ],
        correctIndex: 1,
        explanation: 'NEVER use tub and tile cleaner or grout cleaner on natural stone — these harsh products will damage the surface. Zep is even worse. Only use heavily diluted Simple Green or a tiny bit of Dawn.'
      },
      {
        id: 'q3',
        text: 'Why should you avoid using Zep on natural stone surfaces?',
        options: [
          'It leaves a sticky residue that attracts dirt',
          'It is even harsher than Dawn and will cause serious damage to natural stone',
          'It doesn\'t clean effectively on stone',
          'It is only designed for glass surfaces'
        ],
        correctIndex: 1,
        explanation: 'Zep is even harsher than Dawn on natural stone and will cause serious damage. If Dawn is borderline, Zep is worse. Stick to heavily diluted Simple Green for natural stone.'
      },
      {
        id: 'q4',
        text: 'What is the correct way to clean a shower?',
        options: [
          'Spray tub and tile cleaner and scrub with a tiny brush',
          'Use a wet white rag with some Dawn and Simple Green, scrub down, and rinse off',
          'Use Zep and a black brush, then rinse',
          'Spray glass cleaner all over and wipe with a dry cloth'
        ],
        correctIndex: 1,
        explanation: 'The correct shower method is: take a wet white rag, apply some Dawn and Simple Green, scrub everything down, and rinse off thoroughly.'
      },
      {
        id: 'q5',
        text: 'After scrubbing and rinsing a non-stone shower, what is the finishing step?',
        options: [
          'Leave it to air dry — no further steps needed',
          'Spray tub and tile cleaner for extra shine',
          'Polish with a clean microfiber and glass cleaner if needed',
          'Wipe down with a dry paper towel'
        ],
        correctIndex: 2,
        explanation: 'For non-stone showers, polish with a clean microfiber cloth and glass cleaner if needed for a streak-free finish. NEVER use glass cleaner on natural stone showers — the alcohol in glass cleaner dries out stone.'
      },
      {
        id: 'q5b',
        text: 'Why should you NEVER use glass cleaner on a natural stone shower?',
        options: [
          'It leaves a cloudy residue on stone',
          'Glass cleaner contains alcohol which dries out natural stone and causes damage',
          'It makes the stone too slippery and unsafe',
          'Glass cleaner only works on glass — it won\'t clean stone'
        ],
        correctIndex: 1,
        explanation: 'Glass cleaner contains alcohol that dries out natural stone surfaces and causes damage over time. On natural stone showers, skip the glass cleaner step entirely — just scrub with Dawn and Simple Green, rinse, and dry with a clean microfiber.'
      },
      {
        id: 'q6',
        text: 'What are white brushes designated for?',
        options: [
          'Scrubbing showers and tubs',
          'Toilets only — use them if the client doesn\'t have a toilet brush',
          'General all-purpose scrubbing',
          'Kitchen sinks and stovetops'
        ],
        correctIndex: 1,
        explanation: 'White brushes are for TOILETS ONLY. Only use them if the client doesn\'t have their own toilet brush. Never use a white brush in a shower or on other surfaces.'
      },
      {
        id: 'q7',
        text: 'Which of the following is TRUE about using brushes in showers?',
        options: [
          'Tiny brushes are perfect for getting into shower corners and grout lines',
          'White brushes are the best choice for shower walls',
          'Do NOT use tiny brushes in showers — use a wet white rag instead',
          'Any brush is fine as long as you rinse it after'
        ],
        correctIndex: 2,
        explanation: 'Do NOT use tiny brushes in showers. The correct method is a wet white rag with Dawn and Simple Green. Tiny brushes are not the right tool for shower cleaning.'
      },
      {
        id: 'q8',
        text: 'What can black brushes and white brushes both be used for, besides their primary purpose?',
        options: [
          'Scrubbing natural stone countertops',
          'Cleaning grout',
          'Polishing glass shower doors',
          'Wiping down countertops'
        ],
        correctIndex: 1,
        explanation: 'Both black and white brushes can be used for grout cleaning. This is the one shared use case — otherwise, white brushes are for toilets and should not be used on other surfaces.'
      }
    ],
    status: 'active',
    createdAt: '2026-02-19T10:00:00.000Z',
    updatedAt: '2026-02-19T10:00:00.000Z'
  }
];
